export type { MicroAdapter, PageContext, LiveCart } from './MicroAdapter';
export { EMPTY_CART } from './MicroAdapter';
export { createSallaMicroAdapter } from './SallaMicroAdapter';
export { createZidMicroAdapter } from './ZidMicroAdapter';
export { createWebsiteMicroAdapter } from './WebsiteMicroAdapter';

import type { Platform } from '@hamla/shared/variables';
import type { MicroAdapter } from './MicroAdapter';
import { createSallaMicroAdapter } from './SallaMicroAdapter';
import { createZidMicroAdapter } from './ZidMicroAdapter';
import { createWebsiteMicroAdapter } from './WebsiteMicroAdapter';

/**
 * Detect the storefront platform from window globals, then hostname. Returns a
 * `Platform` — a site with no e-commerce SDK resolves to `'custom'` (a raw
 * website), NOT `'salla'`. This is the single source of truth the SDK uses to
 * pick the adapter AND to report `platform` to the server, so the two can never
 * disagree the way the old hardcoded `'salla'` default made them.
 */
export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'custom';
  if ((window as any).salla || (window as any).Salla) return 'salla';
  if ((window as any).zid || (window as any).store_uuid || (window as any).vitrin) return 'zid';
  const host = window.location?.hostname ?? '';
  if (host.includes('salla')) return 'salla';
  if (host.includes('zid')) return 'zid';
  return 'custom';
}

export function detectAndCreateAdapter(): MicroAdapter {
  if (typeof window === 'undefined') {
    throw new Error('MicroAdapter requires a browser environment');
  }

  switch (detectPlatform()) {
    case 'salla':
      return createSallaMicroAdapter();
    case 'zid':
      return createZidMicroAdapter();
    // A raw website (source/website) or any non-store site — no platform SDK to
    // read. Previously this defaulted to Salla, which mislabeled the platform
    // and read an empty Salla cart on every non-Salla site.
    default:
      return createWebsiteMicroAdapter();
  }
}
